// Pure, deterministic filename transformation primitives.
// All transforms operate on the BASE name and preserve the file extension.

export function splitExt(name, type) {
  if (type === "folder") return [name, ""];
  const i = name.lastIndexOf(".");
  if (i <= 0) return [name, ""]; // no ext, or dotfile like ".env"
  return [name.slice(0, i), name.slice(i)]; // ext includes the dot
}

export function joinExt(base, ext) {
  return `${base}${ext}`;
}

function splitWords(s) {
  return s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
}

const cap = (w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w);

export function toCase(base, style) {
  const words = splitWords(base);
  switch (style) {
    case "title":
      return words.map(cap).join(" ");
    case "lower":
      return base.toLowerCase();
    case "upper":
      return base.toUpperCase();
    case "sentence": {
      const lower = base.toLowerCase();
      return lower.replace(/[A-Za-z]/, (c) => c.toUpperCase());
    }
    case "camel":
      return words
        .map((w, i) => (i === 0 ? w.toLowerCase() : cap(w)))
        .join("");
    case "pascal":
      return words.map(cap).join("");
    case "snake":
      return words.map((w) => w.toLowerCase()).join("_");
    case "kebab":
      return words.map((w) => w.toLowerCase()).join("-");
    default:
      return base;
  }
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function buildFindRegex({ find, caseSensitive, wholeWord, regex }) {
  if (!find) return null;
  let pattern = regex ? find : escapeRegExp(find);
  if (wholeWord) pattern = `\\b${pattern}\\b`;
  const flags = "g" + (caseSensitive ? "" : "i");
  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
}

export function findReplace(base, opts) {
  const re = buildFindRegex(opts);
  if (!re) return base;
  return base.replace(re, opts.replaceWith ?? "");
}

export function removeText(base, { text, caseSensitive }) {
  return findReplace(base, {
    find: text,
    replaceWith: "",
    caseSensitive,
    wholeWord: false,
    regex: false,
  });
}

export function removeChars(base, { mode, count }) {
  const n = Math.max(0, parseInt(count, 10) || 0);
  if (mode === "first") return base.slice(n);
  if (mode === "last") return n ? base.slice(0, -n) : base;
  return base;
}

export function trimName(base, { collapse }) {
  let out = base.trim();
  if (collapse) out = out.replace(/\s+/g, " ");
  return out;
}

export function replaceSeparators(base, { from, to }) {
  let out = base;
  (from || []).forEach((sep) => {
    const re = new RegExp(escapeRegExp(sep), "g");
    out = out.replace(re, to ?? " ");
  });
  return out;
}

export function padNumber(n, padding) {
  const p = Math.max(1, parseInt(padding, 10) || 1);
  return String(n).padStart(p, "0");
}

export function formatNumbered(base, numStr, { position, separator }) {
  const sep = separator ?? " ";
  return position === "end" ? `${base}${sep}${numStr}` : `${numStr}${sep}${base}`;
}
